import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import React, { memo, useCallback, useState } from 'react';

type TodayPosition = 'before' | 'after' | 'visible';

interface PlannerNavigationArrowsProps {
  todayPosition: TodayPosition;
  actualSideBarWidth: number;
  onNavigatePrev?: () => void;
  onNavigateNext?: () => void;
  /** Optional external hook for Today navigation side-effects (e.g., analytics). */
  onNavigateToday?: () => void;
  /** Required core handler that scrolls the planner to today. */
  onTodayClick: () => void;
}

/**
 * PlannerNavigationArrows - Desktop navigation buttons for the planner.
 * Memoized to prevent re-renders on scroll.
 */
export const PlannerNavigationArrows = memo(function PlannerNavigationArrows({
  todayPosition,
  actualSideBarWidth,
  onNavigatePrev,
  onNavigateNext,
  onNavigateToday,
  onTodayClick,
}: PlannerNavigationArrowsProps) {
  const [hoveredNavButton, setHoveredNavButton] = useState<
    'left' | 'right' | 'today' | null
  >(null);

  const handlePrevClick = useCallback(() => {
    onNavigatePrev?.();
  }, [onNavigatePrev]);

  const handleNextClick = useCallback(() => {
    onNavigateNext?.();
  }, [onNavigateNext]);

  const handleTodayClickInternal = useCallback(() => {
    onNavigateToday?.();
    onTodayClick();
  }, [onNavigateToday, onTodayClick]);

  const handlePrevKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onNavigatePrev?.();
      }
    },
    [onNavigatePrev]
  );

  const handleNextKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onNavigateNext?.();
      }
    },
    [onNavigateNext]
  );

  const handleTodayKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onNavigateToday?.();
        onTodayClick();
      }
    },
    [onNavigateToday, onTodayClick]
  );

  return (
    <>
      {/* Left navigation button */}
      <div
        className='absolute top-1/2 z-[40] -translate-y-1/2 pl-2'
        style={{ left: `${actualSideBarWidth + 14}px` }}
      >
        <button
          type='button'
          aria-label='Previous'
          tabIndex={0}
          className={classNames(
            'pointer-events-auto flex h-9 w-9 items-center justify-center bg-white text-secondary transition-all duration-200',
            todayPosition === 'before'
              ? 'rounded-l-full rounded-r-none'
              : 'rounded-full outline outline-1 outline-outline-bright',
            todayPosition === 'before'
              ? 'shadow-[-2px_2px_6px_rgba(0,0,0,0.12)]'
              : 'shadow-md',
            hoveredNavButton === 'left'
              ? classNames(
                  'bg-surface-container-low text-primary',
                  todayPosition === 'before'
                    ? 'shadow-[-3px_3px_12px_rgba(0,0,0,0.18)]'
                    : 'shadow-xl'
                )
              : classNames(
                  'hover:text-primary',
                  todayPosition === 'before'
                    ? 'hover:shadow-[-3px_3px_12px_rgba(0,0,0,0.18)]'
                    : 'hover:shadow-xl'
                ),
            'hover:bg-gray-100 focus:outline-1 focus:outline-outline-variant active:text-primary'
          )}
          onClick={handlePrevClick}
          onMouseEnter={() => setHoveredNavButton('left')}
          onMouseLeave={() => setHoveredNavButton(null)}
          onKeyDown={handlePrevKeyDown}
        >
          <FontAwesomeIcon icon={faChevronLeft as IconProp} />
        </button>
      </div>

      {/* Today button */}
      {todayPosition !== 'visible' && (
        <div
          className='absolute top-1/2 z-[40] -translate-y-1/2'
          style={{
            left:
              todayPosition === 'before'
                ? `${actualSideBarWidth + 50}px`
                : undefined,
            right: todayPosition === 'after' ? '68px' : undefined,
          }}
        >
          <button
            type='button'
            aria-label='Go to today'
            tabIndex={0}
            className={classNames(
              'pointer-events-auto flex h-9 items-center justify-center bg-white text-secondary shadow-md transition-all duration-200',
              todayPosition === 'before'
                ? 'rounded-l-none rounded-r-full'
                : 'rounded-l-full rounded-r-none',
              hoveredNavButton === 'today'
                ? 'bg-surface-container-low text-primary'
                : 'hover:text-primary',
              'hover:bg-gray-100 focus:outline-1 focus:outline-outline-variant'
            )}
            style={{
              paddingLeft: '12px',
              paddingRight: '12px',
              minWidth: '60px',
            }}
            onClick={handleTodayClickInternal}
            onMouseEnter={() => setHoveredNavButton('today')}
            onMouseLeave={() => setHoveredNavButton(null)}
            onKeyDown={handleTodayKeyDown}
          >
            <span className='text-sm font-medium'>Today</span>
          </button>
        </div>
      )}

      {/* Right navigation button */}
      <div className='absolute right-8 top-1/2 z-[40] -translate-y-1/2 pr-2'>
        <button
          type='button'
          aria-label='Next'
          tabIndex={0}
          className={classNames(
            'pointer-events-auto flex h-9 w-9 items-center justify-center bg-white text-secondary transition-all duration-200',
            todayPosition === 'after'
              ? 'rounded-l-none rounded-r-full'
              : 'rounded-full outline outline-1 outline-outline-bright',
            todayPosition === 'after'
              ? 'shadow-[2px_2px_6px_rgba(0,0,0,0.12)]'
              : 'shadow-md',
            hoveredNavButton === 'right'
              ? classNames(
                  'bg-surface-container-low text-primary',
                  todayPosition === 'after'
                    ? 'shadow-[3px_3px_12px_rgba(0,0,0,0.18)]'
                    : 'shadow-xl'
                )
              : classNames(
                  ' hover:text-primary',
                  todayPosition === 'after'
                    ? 'hover:shadow-[3px_3px_12px_rgba(0,0,0,0.18)]'
                    : 'hover:shadow-xl'
                ),
            'hover:bg-gray-100 focus:outline-1 focus:outline-outline-variant active:text-primary'
          )}
          onClick={handleNextClick}
          onMouseEnter={() => setHoveredNavButton('right')}
          onMouseLeave={() => setHoveredNavButton(null)}
          onKeyDown={handleNextKeyDown}
        >
          <FontAwesomeIcon icon={faChevronRight as IconProp} />
        </button>
      </div>
    </>
  );
});

PlannerNavigationArrows.displayName = 'PlannerNavigationArrows';
